import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { api } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Bell, Loader2, Lock, Save } from 'lucide-react';
import { toast } from 'sonner';
import type { JobPositionNotificationSettings } from '@/types';

interface PositionNotificationSettingsSectionProps {
  companyId: string;
  positionId: string;
}

/**
 * PositionNotificationSettingsSection — v3-notif FASE 1 (2026-05-27).
 *
 * Position-level notification toggles. Editable by anyone with `write` or
 * `admin` permission on the company (mirrors the BE gate on
 * `PATCH /v1/companies/:cid/jobPositions/:pid/notification-settings`).
 *
 * Users with `read` permission see the form in readonly mode + a banner so
 * they understand why they cannot save.
 *
 * Default detection: when the position has never had explicit settings, the
 * BE returns the system defaults with the epoch-0 `updatedAt` sentinel. We
 * surface a banner so the recruiter knows their first save will "lock" the
 * current values to this position.
 */

const TOGGLES: ReadonlyArray<{
  key: keyof Pick<
    JobPositionNotificationSettings,
    | 'emitApplicationReceived'
    | 'emitApplicationScreened'
    | 'emitApplicationState'
    | 'emitInterviewLifecycle'
    | 'enableDailyDigest'
  >;
  i18nKey: string;
  hintKey: string;
}> = [
  {
    key: 'emitApplicationReceived',
    i18nKey: 'positionNotif.emitApplicationReceived',
    hintKey: 'positionNotif.emitApplicationReceived.hint',
  },
  {
    key: 'emitApplicationScreened',
    i18nKey: 'positionNotif.emitApplicationScreened',
    hintKey: 'positionNotif.emitApplicationScreened.hint',
  },
  {
    key: 'emitApplicationState',
    i18nKey: 'positionNotif.emitApplicationState',
    hintKey: 'positionNotif.emitApplicationState.hint',
  },
  {
    key: 'emitInterviewLifecycle',
    i18nKey: 'positionNotif.emitInterviewLifecycle',
    hintKey: 'positionNotif.emitInterviewLifecycle.hint',
  },
  {
    key: 'enableDailyDigest',
    i18nKey: 'positionNotif.enableDailyDigest',
    hintKey: 'positionNotif.enableDailyDigest.hint',
  },
];

function isEpochZero(updatedAt: JobPositionNotificationSettings['updatedAt']): boolean {
  if (!updatedAt) return true;
  if (typeof updatedAt === 'string') return false;
  return updatedAt._seconds === 0 && updatedAt._nanoseconds === 0;
}

export const PositionNotificationSettingsSection = ({
  companyId,
  positionId,
}: PositionNotificationSettingsSectionProps) => {
  const { t } = useLanguage();
  const { user } = useAuth();

  // Derive write/admin from the user's company membership. The BE has the
  // final say (403 on PATCH if not write+) — this is purely UX gating.
  const myPermission = user?.memberships?.[companyId]?.permission;
  const canEdit = myPermission === 'admin' || myPermission === 'write';

  const [settings, setSettings] = useState<JobPositionNotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const s = await api.positions.getNotificationSettings(companyId, positionId);
        if (!cancelled) setSettings(s);
      } catch {
        if (!cancelled) toast.error(t('positionNotif.saveError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, positionId]);

  if (loading || !settings) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10 text-muted-foreground gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">{t('common.loading') || 'Loading…'}</span>
        </CardContent>
      </Card>
    );
  }

  const usingDefaults = isEpochZero(settings.updatedAt);

  const toggle = (
    key: typeof TOGGLES[number]['key'],
    v: boolean,
  ) => {
    if (!canEdit) return;
    setSettings((s) => (s ? { ...s, [key]: v } : s));
  };

  const handleSave = async () => {
    if (!settings || !canEdit) return;
    setSaving(true);
    try {
      const saved = await api.positions.updateNotificationSettings(companyId, positionId, {
        emitApplicationReceived: settings.emitApplicationReceived,
        emitApplicationScreened: settings.emitApplicationScreened,
        emitApplicationState: settings.emitApplicationState,
        emitInterviewLifecycle: settings.emitInterviewLifecycle,
        enableDailyDigest: settings.enableDailyDigest,
      });
      setSettings(saved);
      toast.success(t('positionNotif.saved'));
    } catch {
      toast.error(t('positionNotif.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Bell className="h-4 w-4" />
          {t('positionNotif.section.title')}
        </CardTitle>
        <p className="text-xs text-muted-foreground pt-1">
          {t('positionNotif.section.desc')}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {!canEdit && (
          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground flex items-center gap-2">
            <Lock className="h-3.5 w-3.5 shrink-0" />
            {t('positionNotif.readonlyBanner')}
          </div>
        )}
        {canEdit && usingDefaults && (
          <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-primary">
            {t('positionNotif.usingDefaultsBanner')}
          </div>
        )}
        <div className="divide-y divide-border/50">
          {TOGGLES.map((tg) => (
            <div key={tg.key} className="flex items-start justify-between gap-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{t(tg.i18nKey)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t(tg.hintKey)}</p>
              </div>
              <Switch
                checked={Boolean(settings[tg.key])}
                onCheckedChange={(v) => toggle(tg.key, v)}
                disabled={!canEdit}
                aria-label={t(tg.i18nKey)}
              />
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="pt-2">
            <Button onClick={handleSave} disabled={saving} size="sm">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
              {t('positionNotif.save')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PositionNotificationSettingsSection;
